import { useState, useEffect } from 'react'
import {
  MessageCircle,
  CheckCircle,
  XCircle,
  Clock,
  User,
  BookOpen,
  Search,
  Filter,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
  Trash2
} from 'lucide-react'
import AdminLayout from '../../components/layout/AdminLayout'

interface Discussion {
  _id: string
  course: {
    _id: string
    title: string
  }
  lesson: string
  lessonTitle: string
  user: {
    _id: string
    name: string
    email: string
  }
  title: string
  content: string
  replies: Array<{
    user: {
      _id: string
      name: string
      avatar?: string
      role?: string
    }
    content: string
    createdAt: string
    isAdminReply: boolean
  }>
  isResolved: boolean
  resolvedBy?: {
    _id: string
    name: string
  }
  resolvedAt?: string
  videoTimestamp: number
  createdAt: string
  lastActivity: string
}

export default function Discussions() {
  const [discussions, setDiscussions] = useState<Discussion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'resolved' | 'unresolved'>('all')
  const [expandedDiscussions, setExpandedDiscussions] = useState<Set<string>>(new Set())
  const [stats, setStats] = useState({
    total: 0,
    resolved: 0,
    unresolved: 0,
    totalReplies: 0
  })

  // Load discussions
  useEffect(() => {
    loadDiscussions()
    loadStats()
  }, [])

  const loadDiscussions = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/discussions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load discussions')
      }

      const data = await response.json()
      setDiscussions(data.discussions)
    } catch (err) {
      console.error('Error loading discussions:', err)
      setError(err instanceof Error ? err.message : 'Failed to load discussions')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStats({
          total: data.stats.totalDiscussions || 0,
          resolved: data.stats.resolvedDiscussions || 0,
          unresolved: data.stats.unresolvedDiscussions || 0,
          totalReplies: data.stats.totalDiscussionReplies || 0
        })
      }
    } catch (err) {
      console.error('Error loading stats:', err)
    }
  }

  const toggleDiscussion = (discussionId: string) => {
    const newExpanded = new Set(expandedDiscussions)
    if (newExpanded.has(discussionId)) {
      newExpanded.delete(discussionId)
    } else {
      newExpanded.add(discussionId)
    }
    setExpandedDiscussions(newExpanded)
  }

  const resolveDiscussion = async (discussionId: string, resolved: boolean) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/discussions/${discussionId}/resolve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolved })
      })

      if (response.ok) {
        // Update local state
        setDiscussions(prev => prev.map(d =>
          d._id === discussionId
            ? { ...d, isResolved: resolved, resolvedAt: resolved ? new Date().toISOString() : undefined }
            : d
        ))
        loadStats() // Refresh stats
      }
    } catch (err) {
      console.error('Error resolving discussion:', err)
    }
  }

  const deleteDiscussion = async (discussionId: string) => {
    if (!confirm('Are you sure you want to delete this discussion?')) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/discussions/${discussionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setDiscussions(prev => prev.filter(d => d._id !== discussionId))
        loadStats() // Refresh stats
      }
    } catch (err) {
      console.error('Error deleting discussion:', err)
    }
  }

  const addReply = async (discussionId: string, content: string) => {
    try {
      console.log('Adding reply:', { discussionId, content })

      if (!content || !content.trim()) {
        alert('Reply content cannot be empty')
        return
      }

      const token = localStorage.getItem('token')
      const response = await fetch(`/api/discussions/${discussionId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: content.trim() })
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Reply added successfully:', data)
        // Update local state
        setDiscussions(prev => prev.map(d =>
          d._id === discussionId ? data.discussion : d
        ))
        loadStats() // Refresh stats
      } else {
        const error = await response.json()
        console.error('Error response:', error)
        alert(`Failed to add reply: ${error.error || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Error adding reply:', err)
      alert('Failed to add reply. Please try again.')
    }
  }

  // Filter discussions
  const filteredDiscussions = discussions.filter(discussion => {
    const matchesSearch = discussion.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      discussion.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (discussion.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (discussion.course?.title || '').toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'resolved' && discussion.isResolved) ||
      (statusFilter === 'unresolved' && !discussion.isResolved)

    return matchesSearch && matchesStatus
  })

  const formatTime = (timestamp: number) => {
    const minutes = Math.floor(timestamp / 60)
    const seconds = Math.floor(timestamp % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-rose-200 dark:border-rose-800 rounded-full animate-spin border-t-rose-600 dark:border-t-rose-400"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Discussions</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={loadDiscussions}
          className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-charcoal-900 dark:text-white mb-2">
            Discussion Management
          </h1>
          <p className="text-charcoal-600 dark:text-charcoal-400">
            Manage course discussions and student questions
          </p>
        </div>
        <button
          onClick={() => { loadDiscussions(); loadStats() }}
          className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700 text-charcoal-600 dark:text-charcoal-300 rounded-xl transition-all shadow-sm hover:shadow-md font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-charcoal-500 dark:text-charcoal-400">Total Discussions</p>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-charcoal-500 dark:text-charcoal-400">Resolved</p>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.resolved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
              <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-charcoal-500 dark:text-charcoal-400">Unresolved</p>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.unresolved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
              <User className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-charcoal-500 dark:text-charcoal-400">Total Replies</p>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.totalReplies}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 border border-gray-100 dark:border-dark-700 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal-400" />
              <input
                type="text"
                placeholder="Search discussions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white placeholder-charcoal-400 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50">
              <Filter className="w-4 h-4 text-charcoal-500 dark:text-charcoal-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="bg-transparent border-none text-charcoal-900 dark:text-white focus:ring-0 p-0 text-sm font-medium cursor-pointer"
              >
                <option value="all">All Discussions</option>
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Discussions List */}
      <div className="space-y-4">
        {filteredDiscussions.length === 0 ? (
          <div className="bg-white dark:bg-dark-800 rounded-3xl p-12 text-center border border-gray-100 dark:border-dark-700 shadow-sm">
            <div className="w-16 h-16 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-charcoal-400" />
            </div>
            <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">No Discussions Found</h3>
            <p className="text-charcoal-500 dark:text-charcoal-400">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No discussions have been created yet'
              }
            </p>
          </div>
        ) : (
          filteredDiscussions.map((discussion) => (
            <div key={discussion._id} className="bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700 overflow-hidden shadow-sm hover:shadow-md transition-all">
              {/* Discussion Header */}
              <div className="p-6 border-b border-gray-100 dark:border-dark-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <button
                        onClick={() => toggleDiscussion(discussion._id)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                      >
                        {expandedDiscussions.has(discussion._id) ? (
                          <ChevronDown className="w-5 h-5 text-charcoal-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-charcoal-400" />
                        )}
                      </button>
                      <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">{discussion.title}</h3>
                      {discussion.isResolved ? (
                        <span className="px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-bold rounded-full">
                          Resolved
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-bold rounded-full">
                          Unresolved
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-charcoal-500 dark:text-charcoal-400 mb-4">
                      <div className="flex items-center gap-1.5">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{discussion.user?.name || 'Unknown User'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="w-4 h-4" />
                        <span>{discussion.course?.title || 'Unknown Course'} • {discussion.lessonTitle}</span>
                      </div>
                      {discussion.videoTimestamp > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          <span className="font-mono">{formatTime(discussion.videoTimestamp)}</span>
                        </div>
                      )}
                      <div className="text-charcoal-400">
                        {formatDate(discussion.createdAt)}
                      </div>
                    </div>

                    <p className="text-charcoal-700 dark:text-charcoal-300 line-clamp-2 leading-relaxed">{discussion.content}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {!discussion.isResolved ? (
                      <button
                        onClick={() => resolveDiscussion(discussion._id, true)}
                        className="p-2.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                        title="Mark as resolved"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => resolveDiscussion(discussion._id, false)}
                        className="p-2.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-xl transition-colors"
                        title="Mark as unresolved"
                      >
                        <Clock className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteDiscussion(discussion._id)}
                      className="p-2.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                      title="Delete discussion"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Discussion Details */}
              {expandedDiscussions.has(discussion._id) && (
                <div className="p-6 bg-gray-50 dark:bg-dark-900/30 animate-slide-down">
                  {/* Replies */}
                  <div className="space-y-6 mb-8">
                    <h4 className="font-bold text-charcoal-900 dark:text-white flex items-center gap-2">
                      Replies <span className="px-2 py-0.5 bg-gray-200 dark:bg-dark-700 rounded-full text-xs">{discussion.replies.length}</span>
                    </h4>

                    {discussion.replies.length === 0 ? (
                      <p className="text-charcoal-500 dark:text-charcoal-400 text-sm italic">No replies yet</p>
                    ) : (
                      discussion.replies.map((reply, index) => (
                        <div key={index} className="bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-rose-50 dark:bg-rose-900/20 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-sm font-bold text-rose-600 dark:text-rose-400">
                                {reply.user?.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-bold text-charcoal-900 dark:text-white">{reply.user?.name || 'Unknown User'}</span>
                                {reply.isAdminReply && (
                                  <span className="px-2 py-0.5 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-xs font-bold rounded-md">
                                    Admin
                                  </span>
                                )}
                                <span className="text-xs text-charcoal-400">
                                  • {formatDate(reply.createdAt)}
                                </span>
                              </div>
                              <p className="text-charcoal-700 dark:text-charcoal-300 leading-relaxed">{reply.content}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Reply */}
                  <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm">
                    <h4 className="font-bold text-charcoal-900 dark:text-white mb-4">Add Reply</h4>
                    <ReplyForm
                      onSubmit={(content) => addReply(discussion._id, content)}
                    />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// Reply Form Component
function ReplyForm({ onSubmit }: { onSubmit: (content: string) => void }) {
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!content.trim()) return

    setSubmitting(true)
    try {
      await onSubmit(content.trim())
      setContent('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Type your reply..."
        rows={3}
        className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white placeholder-charcoal-400 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all resize-none"
        required
      />
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting || !content.trim()}
          className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 dark:disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30 disabled:shadow-none"
        >
          {submitting ? 'Posting...' : 'Post Reply'}
        </button>
      </div>
    </form>
  )
}
